/**
 * Render a plain-text string as an array of React nodes, converting
 * any `http://` / `https://` prefixed substrings into `<a target="_blank">`
 * links while leaving surrounding text plain.
 *
 * Deliberately conservative — we do NOT match bare "www." or protocol-
 * less domains, since vendor-authored terms can legitimately contain
 * "example.com" as prose without wanting it turned into a hyperlink.
 *
 * Trailing punctuation (`.` `,` `)` `]` `}` `!` `?` `;` `:`) is
 * stripped from the matched URL and preserved as text so
 * "Visit https://example.com/." doesn't turn the trailing period into
 * part of the URL.
 *
 * @param {string} text — raw text to linkify. Empty / non-string
 *   inputs return an empty array.
 * @param {object} [options]
 * @param {string} [options.className] — className applied to every
 *   generated <a> so callers can style them contextually.
 * @returns {Array<string|React.ReactElement>}
 */
import React from "react";

const URL_REGEX = /(https?:\/\/[^\s<>"']+)/gi;
const TRAILING_PUNCT = /[.,)\]}!?;:]+$/;

export function linkifyText(text, options = {}) {
  const { className = "underline decoration-dotted underline-offset-2 hover:decoration-solid" } = options;
  const src = typeof text === "string" ? text : "";
  if (!src) return [];
  const out = [];
  let cursor = 0;
  let match;
  URL_REGEX.lastIndex = 0;
  while ((match = URL_REGEX.exec(src)) !== null) {
    const start = match.index;
    const raw = match[0];
    // Peel trailing punctuation off the URL and push it back as text.
    let url = raw;
    let trailing = "";
    const punct = url.match(TRAILING_PUNCT);
    if (punct) {
      trailing = punct[0];
      url = url.slice(0, -trailing.length);
    }
    if (start > cursor) out.push(src.slice(cursor, start));
    out.push(
      React.createElement(
        "a",
        {
          key: `lnk-${start}`,
          href: url,
          target: "_blank",
          rel: "noopener noreferrer",
          className,
        },
        url,
      ),
    );
    if (trailing) out.push(trailing);
    cursor = start + raw.length;
  }
  if (cursor < src.length) out.push(src.slice(cursor));
  return out;
}
